import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ApiService, AppointmentModel, FacilityService, getAvailableTimeIntervalsForLocations, Patient, validateWeekdays, Waitlist } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, combineLatestWith, map, of, startWith, Subject } from 'rxjs'

export interface AppointmentWaitlistDialogData {
  appointment: AppointmentModel
  patient: Patient
}

export interface AppointmentWaitlistDialogResult {
  preferences: Waitlist.PatientWaitListPreferences
  appointment: AppointmentModel
}

@Component({
  selector: 'mmx-appointment-waitlist-dialog',
  templateUrl: './appointment-waitlist-dialog.component.html',
  standalone: false,
})
export class AppointmentWaitlistDialogComponent implements OnInit, OnDestroy {
  public formGroup = this.fb.group({
    locations: this.fb.control<string[]>([], Validators.required),
    weekdays: this.fb.control<Waitlist.PatientAvailability>(null, [Validators.required, validateWeekdays()]),
  })

  public locations$: BehaviorSubject<any[]> = this.facilityService.facilities$

  public locationsOpen$ = combineLatest([
    this.locations$,
    this.formGroup.get('locations').valueChanges.pipe(
      startWith([]),
    ),
  ])
    .pipe(
      map(([locations]) => {
        const selectedLocationIds = this.formGroup.get('locations').value
        if (!selectedLocationIds.length) {
          return locations
        }

        return locations.filter((location) => selectedLocationIds.includes(location.id))
      }),
      map(getAvailableTimeIntervalsForLocations),
    )

  private onDestroy$ = new Subject<void>()

  constructor(
    public dialogRef: MatDialogRef<AppointmentWaitlistDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AppointmentWaitlistDialogData,
    private fb: FormBuilder,
    private facilityService: FacilityService,
    private restService: ApiService,
    private snackbar: MatSnackBar,
    private translateService: TranslateService,
  ) { }

  public ngOnInit() {
    if (this.data.patient.waitlistPreferences) {
      this.formGroup.patchValue({
        ...this.data.patient.waitlistPreferences,
      })
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onSubmit() {
    const type = this.data.appointment.waitList ? 'update' : 'new'
    const { locations, weekdays } = this.formGroup.value
    // Update patient's waitListPreferences directly from form values.
    this.data.patient.waitlistPreferences = { locations, weekdays }

    const obs = this.data.appointment.waitList
      ? of(this.data.appointment)
      : this.restService.patchAppointment(this.data.appointment.id, { waitList: true })

    this.restService.updatePatient(this.data.patient).pipe(
      combineLatestWith(obs),
    ).subscribe({
      next: ([, appointment]) => {
        this.openSnackbar(type === 'new' ? 'dialog.waitlist.status.added' : 'dialog.waitlist.status.updated')
        this.dialogRef.close({
          preferences: this.formGroup.value,
          appointment,
        })
      },
      error: () => this.openSnackbar('dialog.waitlist.status.error'),
    })
  }

  private openSnackbar(message: string) {
    this.snackbar.open(
      this.translateService.instant(message),
      null,
      { duration: 3000 },
    )
  }
}
